import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, query, where, getDocs, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import './VillageDashboard.css';

export default function VillageDashboard() {
  const { villageCode } = useParams();
  const decodedVillageCode = decodeURIComponent(villageCode);
  const [activeTab, setActiveTab] = useState('entry'); // 'farmers', 'entry', 'report'
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  
  // Data States
  const [farmers, setFarmers] = useState([]);
  const [villageName, setVillageName] = useState('');
  const [loading, setLoading] = useState(true);

  // Form States
  const [newFarmerName, setNewFarmerName] = useState('');
  const [newFarmerId, setNewFarmerId] = useState('');
  const [newFarmerEmail, setNewFarmerEmail] = useState('');
  const [newFarmerPhone, setNewFarmerPhone] = useState('');
  
  // Edit States
  const [editingFarmer, setEditingFarmer] = useState(null);
  const [editFarmerName, setEditFarmerName] = useState('');
  const [editFarmerId, setEditFarmerId] = useState('');
  
  // Edit Milk Entry States
  const [editingEntry, setEditingEntry] = useState(null);
  const [editEntryQty, setEditEntryQty] = useState('');
  const [editEntryRate, setEditEntryRate] = useState('');
  const [editEntryDate, setEditEntryDate] = useState('');
  const [editEntryShift, setEditEntryShift] = useState('');
  
  const [entryFarmerId, setEntryFarmerId] = useState('');
  const [qty, setQty] = useState('');
  const [rate, setRate] = useState('');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [entryShift, setEntryShift] = useState('morning');
  
  const [reportFromDate, setReportFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportToDate, setReportToDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedFarmerId, setSelectedFarmerId] = useState('');
  const [reportData, setReportData] = useState([]);
  const [dailySummary, setDailySummary] = useState({ totalLiters: 0, totalAmount: 0 });
  const [farmerReportData, setFarmerReportData] = useState([]);
  const [farmerReportSummary, setFarmerReportSummary] = useState({ totalFarmers: 0, totalLiters: 0, totalAmount: 0 });
  const [recentEntries, setRecentEntries] = useState([]);
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [farmerAccountData, setFarmerAccountData] = useState([]);

  // Check authentication and fetch farmers
  useEffect(() => {
    const villageUser = localStorage.getItem('villageUser');
    if (!villageUser) {
      navigate('/');
      return;
    }
    
    const fetchVillageName = async () => {
      try {
        const q = query(collection(db, "villages"), where("code", "==", decodedVillageCode));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const villageData = snapshot.docs[0].data();
          setVillageName(villageData.name || '');
        }
      } catch (error) {
        console.error("Error fetching village name:", error);
      }
    };

    const fetchFarmers = async () => {
      try {
        const q = query(collection(db, "farmers"), where("villageCode", "==", decodedVillageCode));
        const snapshot = await getDocs(q);
        setFarmers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
      } catch (error) {
        console.error("Error fetching farmers:", error);
        setLoading(false);
      }
    };

    fetchVillageName();
    fetchFarmers();
  }, [decodedVillageCode, navigate]);

  // Calculate daily summary
  useEffect(() => {
    if (reportData.length > 0) {
      const totalLiters = reportData.reduce((sum, entry) => sum + entry.quantity, 0);
      const totalAmount = reportData.reduce((sum, entry) => sum + entry.totalAmount, 0);
      setDailySummary({ totalLiters, totalAmount });
    }
  }, [reportData]);

  // Fetch recent entries for aside section
  useEffect(() => {
    const fetchRecentEntries = async () => {
      try {
        const q = query(
          collection(db, "milk_entries"), 
          where("villageCode", "==", decodedVillageCode)
        );
        const snapshot = await getDocs(q);
        const entries = snapshot.docs.map(doc => doc.data());
        // Sort by timestamp client-side to avoid composite index requirement
        const sortedEntries = entries.sort((a, b) => {
          const timeA = a.timestamp?.seconds || 0;
          const timeB = b.timestamp?.seconds || 0;
          return timeB - timeA;
        });
        
        // Filter to show entries for the selected date and shift
        const selectedDate = entryDate;
        const selectedShift = entryShift;
        
        const filteredEntries = sortedEntries.filter(entry => 
          entry.date === selectedDate && entry.shift === selectedShift
        );
        
        setRecentEntries(filteredEntries.slice(0, 5)); // Show last 5 entries for selected date and shift
      } catch (error) {
        console.error("Error fetching recent entries:", error);
      }
    };
    fetchRecentEntries();
  }, [decodedVillageCode, entryDate, entryShift]);

  // --- ACTIONS ---

  const addFarmer = async (e) => {
    e.preventDefault();
    if (!newFarmerName.trim() || !newFarmerId.trim() || !newFarmerEmail.trim() || !newFarmerPhone.trim()) {
      setMessage('Please fill all fields including email and phone number');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newFarmerEmail)) {
      setMessage('Please enter a valid email address');
      return;
    }

    // Basic phone validation (10 digits)
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(newFarmerPhone)) {
      setMessage('Please enter a valid 10-digit phone number');
      return;
    }

    try {
      await addDoc(collection(db, "farmers"), {
        name: newFarmerName.trim(),
        farmerId: newFarmerId.trim().toUpperCase(),
        email: newFarmerEmail.trim(),
        phone: newFarmerPhone.trim(),
        villageCode: decodedVillageCode,
        createdAt: new Date()
      });
      
      setFarmers([...farmers, { 
        name: newFarmerName.trim(), 
        farmerId: newFarmerId.trim().toUpperCase(),
        email: newFarmerEmail.trim(),
        phone: newFarmerPhone.trim(),
        villageCode: decodedVillageCode 
      }]);
      setNewFarmerName('');
      setNewFarmerId('');
      setNewFarmerEmail('');
      setNewFarmerPhone('');
      setMessage('Farmer added successfully!');
    } catch (error) {
      console.error("Error adding farmer:", error);
      setMessage('Error adding farmer. Please try again.');
    }
  };

  const addMilkEntry = async (e) => {
    e.preventDefault();
    if (!entryFarmerId || !qty || !rate) {
      setMessage('Please fill all fields');
      return;
    }

    const quantity = parseFloat(qty);
    const rateValue = parseFloat(rate);
    const total = quantity * rateValue;
    
    try {
      await addDoc(collection(db, "milk_entries"), {
        villageCode: decodedVillageCode,
        farmerId: entryFarmerId,
        date: entryDate,
        shift: entryShift,
        timestamp: new Date(),
        quantity: quantity,
        rate: rateValue,
        totalAmount: total
      });
      
      setMessage(`Entry saved successfully! Total: ₹${total.toFixed(2)}`);
      setQty('');
      setRate('');
      
      // Refresh report if currently viewing
      if (activeTab === 'report') {
        generateReport();
      }
    } catch (error) {
      console.error("Error adding milk entry:", error);
      setMessage('Error saving entry. Please try again.');
    }
  };

  const generateReport = async () => {
    try {
      const q = query(
        collection(db, "milk_entries"), 
        where("villageCode", "==", decodedVillageCode),
        where("date", "==", reportDate)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => doc.data());
      // Sort by timestamp client-side to avoid composite index requirement
      const sortedData = data.sort((a, b) => {
        const timeA = a.timestamp?.seconds || 0;
        const timeB = b.timestamp?.seconds || 0;
        return timeB - timeA;
      });
      setReportData(sortedData);
      
      if (sortedData.length === 0) {
        setMessage('No entries found for this date.');
      } else {
        setMessage(`Found ${sortedData.length} entries for ${reportDate}`);
      }
    } catch (error) {
      console.error("Error generating report:", error);
      setMessage('Error generating report. Please try again.');
    }
  };

  const generateDateRangeReport = async () => {
    try {
      // Validate date range
      const fromDate = new Date(reportFromDate);
      const toDate = new Date(reportToDate);
      
      if (fromDate > toDate) {
        setMessage('From date cannot be greater than To date.');
        return;
      }

      // Query all entries for the village
      const q = query(
        collection(db, "milk_entries"), 
        where("villageCode", "==", decodedVillageCode)
      );
      const snapshot = await getDocs(q);
      const allData = snapshot.docs.map(doc => doc.data());
      
      // Filter by date range - convert to date strings for comparison
      const filteredData = allData.filter(entry => {
        if (!entry.date) return false;
        
        const entryDate = new Date(entry.date);
        // Set time to 00:00:00 for date-only comparison
        entryDate.setHours(0, 0, 0, 0);
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(0, 0, 0, 0);
        
        return entryDate >= fromDate && entryDate <= toDate;
      });
      
      // Group entries by farmer and merge them
      const farmerGroups = {};
      filteredData.forEach(entry => {
        if (!farmerGroups[entry.farmerId]) {
          farmerGroups[entry.farmerId] = {
            farmerId: entry.farmerId,
            entries: [],
            totalQuantity: 0,
            totalAmount: 0,
            totalRate: 0
          };
        }
        farmerGroups[entry.farmerId].entries.push(entry);
        farmerGroups[entry.farmerId].totalQuantity += entry.quantity;
        farmerGroups[entry.farmerId].totalAmount += entry.totalAmount;
        farmerGroups[entry.farmerId].totalRate += entry.rate;
      });

      // Convert to array and sort by total amount
      const mergedData = Object.values(farmerGroups).sort((a, b) => b.totalAmount - a.totalAmount);
      
      // Add farmer names and calculate mean rate
      const mergedDataWithNames = mergedData.map(farmerData => {
        const farmer = farmers.find(f => f.farmerId === farmerData.farmerId);
        const meanRate = farmerData.entries.length > 0 ? 
          farmerData.totalRate / farmerData.entries.length : 0;
        
        return {
          ...farmerData,
          farmerName: farmer ? farmer.name : farmerData.farmerId,
          meanRate: parseFloat(meanRate.toFixed(2))
        };
      });

      setReportData(mergedDataWithNames);
      
      if (mergedDataWithNames.length === 0) {
        setMessage(`No entries found between ${reportFromDate} and ${reportToDate}.`);
      } else {
        setMessage(`Date range report generated - ${mergedDataWithNames.length} farmers with merged entries`);
      }
    } catch (error) {
      console.error("Error generating date range report:", error);
      setMessage('Error generating report. Please try again.');
    }
  };

  const generateFarmerReport = async () => {
    try {
      const q = query(
        collection(db, "milk_entries"), 
        where("villageCode", "==", decodedVillageCode),
        where("date", "==", reportDate)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => doc.data());
      
      // Group entries by farmer
      const farmerGroups = {};
      data.forEach(entry => {
        if (!farmerGroups[entry.farmerId]) {
          farmerGroups[entry.farmerId] = {
            farmerId: entry.farmerId,
            entries: [],
            totalQuantity: 0,
            totalAmount: 0
          };
        }
        farmerGroups[entry.farmerId].entries.push(entry);
        farmerGroups[entry.farmerId].totalQuantity += entry.quantity;
        farmerGroups[entry.farmerId].totalAmount += entry.totalAmount;
      });

      // Convert to array and sort by total amount
      const farmerReport = Object.values(farmerGroups).sort((a, b) => b.totalAmount - a.totalAmount);
      
      // Add farmer names
      const farmerReportWithNames = farmerReport.map(farmerData => {
        const farmer = farmers.find(f => f.farmerId === farmerData.farmerId);
        return {
          ...farmerData,
          farmerName: farmer ? farmer.name : farmerData.farmerId
        };
      });

      setFarmerReportData(farmerReportWithNames);
      
      // Calculate summary
      const totalFarmers = farmerReportWithNames.length;
      const totalLiters = farmerReportWithNames.reduce((sum, farmer) => sum + farmer.totalQuantity, 0);
      const totalAmount = farmerReportWithNames.reduce((sum, farmer) => sum + farmer.totalAmount, 0);
      
      setFarmerReportSummary({
        totalFarmers,
        totalLiters,
        totalAmount
      });
      
      if (farmerReportWithNames.length === 0) {
        setMessage('No farmer entries found for this date.');
      } else {
        setMessage(`Farmer report generated for ${reportDate} - ${farmerReportWithNames.length} farmers`);
      }
    } catch (error) {
      console.error("Error generating farmer report:", error);
      setMessage('Error generating farmer report. Please try again.');
    }
  };

  const generateFarmerReportByShift = async () => {
    try {
      const q = query(
        collection(db, "milk_entries"), 
        where("villageCode", "==", decodedVillageCode),
        where("date", "==", reportDate),
        where("shift", "==", reportShift)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => doc.data());
      
      // Group entries by farmer
      const farmerGroups = {};
      data.forEach(entry => {
        if (!farmerGroups[entry.farmerId]) {
          farmerGroups[entry.farmerId] = {
            farmerId: entry.farmerId,
            entries: [],
            totalQuantity: 0,
            totalAmount: 0
          };
        }
        farmerGroups[entry.farmerId].entries.push(entry);
        farmerGroups[entry.farmerId].totalQuantity += entry.quantity;
        farmerGroups[entry.farmerId].totalAmount += entry.totalAmount;
      });

      // Convert to array and sort by total amount
      const farmerReport = Object.values(farmerGroups).sort((a, b) => b.totalAmount - a.totalAmount);
      
      // Add farmer names
      const farmerReportWithNames = farmerReport.map(farmerData => {
        const farmer = farmers.find(f => f.farmerId === farmerData.farmerId);
        return {
          ...farmerData,
          farmerName: farmer ? farmer.name : farmerData.farmerId
        };
      });

      setFarmerReportData(farmerReportWithNames);
      
      // Calculate summary
      const totalFarmers = farmerReportWithNames.length;
      const totalLiters = farmerReportWithNames.reduce((sum, farmer) => sum + farmer.totalQuantity, 0);
      const totalAmount = farmerReportWithNames.reduce((sum, farmer) => sum + farmer.totalAmount, 0);
      
      setFarmerReportSummary({
        totalFarmers,
        totalLiters,
        totalAmount
      });
      
      if (farmerReportWithNames.length === 0) {
        setMessage(`No farmer entries found for ${reportDate} - ${reportShift} shift.`);
      } else {
        setMessage(`Farmer report generated for ${reportDate} ${reportShift} shift - ${farmerReportWithNames.length} farmers`);
      }
    } catch (error) {
      console.error("Error generating farmer report by shift:", error);
      setMessage('Error generating farmer report. Please try again.');
    }
  };

  const generateFarmerSpecificReport = async () => {
    try {
      if (!selectedFarmerId) {
        setMessage('Please select a farmer to view their data.');
        return;
      }

      // Validate date range
      const fromDate = new Date(reportFromDate);
      const toDate = new Date(reportToDate);
      
      if (fromDate > toDate) {
        setMessage('From date cannot be greater than To date.');
        return;
      }

      // Query all entries for the specific farmer
      const q = query(
        collection(db, "milk_entries"), 
        where("villageCode", "==", decodedVillageCode),
        where("farmerId", "==", selectedFarmerId)
      );
      const snapshot = await getDocs(q);
      const allData = snapshot.docs.map(doc => doc.data());
      
      // Filter by date range - convert to date strings for comparison
      const filteredData = allData.filter(entry => {
        if (!entry.date) return false;
        
        const entryDate = new Date(entry.date);
        // Set time to 00:00:00 for date-only comparison
        entryDate.setHours(0, 0, 0, 0);
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(0, 0, 0, 0);
        
        return entryDate >= fromDate && entryDate <= toDate;
      });
      
      // Sort by timestamp client-side to avoid composite index requirement
      const sortedData = filteredData.sort((a, b) => {
        const timeA = a.timestamp?.seconds || 0;
        const timeB = b.timestamp?.seconds || 0;
        return timeB - timeA;
      });
      
      setReportData(sortedData);
      
      // Calculate summary for this specific farmer
      const totalQuantity = sortedData.reduce((sum, entry) => sum + entry.quantity, 0);
      const totalAmount = sortedData.reduce((sum, entry) => sum + entry.totalAmount, 0);
      const avgRate = totalQuantity > 0 ? totalAmount / totalQuantity : 0;
      
      const farmer = farmers.find(f => f.farmerId === selectedFarmerId);
      const farmerName = farmer ? farmer.name : selectedFarmerId;
      
      setFarmerReportSummary({
        totalFarmers: 1,
        totalLiters: totalQuantity,
        totalAmount: totalAmount
      });
      
      if (sortedData.length === 0) {
        setMessage(`No entries found for ${farmerName} between ${reportFromDate} and ${reportToDate}.`);
      } else {
        setMessage(`Farmer data for ${farmerName} - ${sortedData.length} entries between ${reportFromDate} and ${reportToDate}`);
      }
    } catch (error) {
      console.error("Error generating farmer specific report:", error);
      setMessage('Error generating farmer report. Please try again.');
    }
  };

  const generateFarmerAccountData = async () => {
    try {
      if (!selectedFarmerId) {
        setMessage('Please select a farmer to view their account.');
        return;
      }

      // Validate date range
      const fromDate = new Date(reportFromDate);
      const toDate = new Date(reportToDate);
      
      if (fromDate > toDate) {
        setMessage('From date cannot be greater than To date.');
        return;
      }

      // Query all entries for the specific farmer
      const q = query(
        collection(db, "milk_entries"), 
        where("villageCode", "==", decodedVillageCode),
        where("farmerId", "==", selectedFarmerId)
      );
      const snapshot = await getDocs(q);
      const allData = snapshot.docs.map(doc => doc.data());
      
      // Filter by date range - convert to date strings for comparison
      const filteredData = allData.filter(entry => {
        if (!entry.date) return false;
        
        const entryDate = new Date(entry.date);
        // Set time to 00:00:00 for date-only comparison
        entryDate.setHours(0, 0, 0, 0);
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(0, 0, 0, 0);
        
        return entryDate >= fromDate && entryDate <= toDate;
      });
      
      // Sort by timestamp client-side to avoid composite index requirement
      const sortedData = filteredData.sort((a, b) => {
        const timeA = a.timestamp?.seconds || 0;
        const timeB = b.timestamp?.seconds || 0;
        return timeB - timeA;
      });
      
      setFarmerAccountData(sortedData);
      
      if (sortedData.length === 0) {
        setMessage(`No account data found for ${selectedFarmer.name} between ${reportFromDate} and ${reportToDate}.`);
      } else {
        setMessage(`Account data loaded for ${selectedFarmer.name} - ${sortedData.length} entries between ${reportFromDate} and ${reportToDate}`);
      }
    } catch (error) {
      console.error("Error generating farmer account data:", error);
      setMessage('Error generating farmer account data. Please try again.');
    }
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm('Are you sure you want to delete this milk entry? This action cannot be undone.')) {
      return;
    }

    try {
      // Find the document ID for this entry
      const q = query(
        collection(db, "milk_entries"),
        where("villageCode", "==", decodedVillageCode),
        where("farmerId", "==", entry.farmerId),
        where("date", "==", entry.date),
        where("shift", "==", entry.shift),
        where("quantity", "==", entry.quantity),
        where("rate", "==", entry.rate),
        where("totalAmount", "==", entry.totalAmount)
      );
      
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const docRef = snapshot.docs[0];
        await deleteDoc(doc(db, "milk_entries", docRef.id));
        
        setMessage('Entry deleted successfully!');
        
        // Refresh current report
        if (activeTab === 'report') {
          if (reportDate) {
            generateReport();
          } else {
            generateDateRangeReport();
          }
        }
      } else {
        setMessage('Error: Could not find entry to delete.');
      }
    } catch (error) {
      console.error("Error deleting entry:", error);
      setMessage('Error deleting entry. Please try again.');
    }
  };

  const deleteFarmer = async (farmer) => {
    if (!window.confirm(`Are you sure you want to delete farmer ${farmer.name}? This will also delete all their milk entries.`)) {
      return;
    }

    try {
      // Delete all milk entries for this farmer
      const entriesQuery = query(
        collection(db, "milk_entries"),
        where("villageCode", "==", decodedVillageCode),
        where("farmerId", "==", farmer.farmerId)
      );
      const entriesSnapshot = await getDocs(entriesQuery);
      
      // Delete all entries
      const deletePromises = entriesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      // Delete the farmer
      await deleteDoc(doc(db, "farmers", farmer.id));
      
      // Update local state
      setFarmers(farmers.filter(f => f.id !== farmer.id));
      setMessage(`Farmer ${farmer.name} and all their entries deleted successfully!`);
      
      // Refresh report if currently viewing
      if (activeTab === 'report') {
        if (reportDate) {
          generateReport();
        } else {
          generateDateRangeReport();
        }
      }
    } catch (error) {
      console.error("Error deleting farmer:", error);
      setMessage('Error deleting farmer. Please try again.');
    }
  };

  const updateFarmer = async (farmerId, updates) => {
    try {
      const farmerRef = doc(db, "farmers", farmerId);
      await updateDoc(farmerRef, updates);
      
      // Update local state
      setFarmers(farmers.map(farmer => 
        farmer.id === farmerId ? { ...farmer, ...updates } : farmer
      ));
      setMessage('Farmer updated successfully!');
    } catch (error) {
      console.error("Error updating farmer:", error);
      setMessage('Error updating farmer. Please try again.');
    }
  };

  const startEditFarmer = (farmer) => {
    setEditingFarmer(farmer.id);
    setEditFarmerName(farmer.name);
    setEditFarmerId(farmer.farmerId);
  };

  const cancelEditFarmer = () => {
    setEditingFarmer(null);
    setEditFarmerName('');
    setEditFarmerId('');
  };

  const saveEditFarmer = async (farmerId) => {
    if (!editFarmerName.trim() || !editFarmerId.trim()) {
      setMessage('Please fill all fields');
      return;
    }

    try {
      await updateFarmer(farmerId, {
        name: editFarmerName.trim(),
        farmerId: editFarmerId.trim().toUpperCase()
      });
      
      setEditingFarmer(null);
      setEditFarmerName('');
      setEditFarmerId('');
    } catch (error) {
      console.error("Error saving farmer edit:", error);
      setMessage('Error saving farmer. Please try again.');
    }
  };

  const startEditEntry = (entry) => {
    setEditingEntry(entry);
    setEditEntryQty(entry.quantity.toString());
    setEditEntryRate(entry.rate.toString());
    setEditEntryDate(entry.date);
    setEditEntryShift(entry.shift || 'morning');
  };

  const cancelEditEntry = () => {
    setEditingEntry(null);
    setEditEntryQty('');
    setEditEntryRate('');
    setEditEntryDate('');
    setEditEntryShift('');
  };

  const saveEditEntry = async (entry) => {
    if (!editEntryQty || !editEntryRate || !editEntryDate || !editEntryShift) {
      setMessage('Please fill all fields');
      return;
    }

    const quantity = parseFloat(editEntryQty);
    const rateValue = parseFloat(editEntryRate);
    const total = quantity * rateValue;

    try {
      // Find the document ID for this entry
      const q = query(
        collection(db, "milk_entries"),
        where("villageCode", "==", decodedVillageCode),
        where("farmerId", "==", entry.farmerId),
        where("date", "==", entry.date),
        where("shift", "==", entry.shift),
        where("quantity", "==", entry.quantity),
        where("rate", "==", entry.rate),
        where("totalAmount", "==", entry.totalAmount)
      );
      
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const docRef = snapshot.docs[0];
        await updateDoc(docRef.ref, {
          quantity: quantity,
          rate: rateValue,
          totalAmount: total,
          date: editEntryDate,
          shift: editEntryShift
        });
        
        setMessage('Entry updated successfully!');
        
        // Refresh current report
        if (activeTab === 'report') {
          if (reportDate) {
            generateReport();
          } else {
            generateDateRangeReport();
          }
        }
      } else {
        setMessage('Error: Could not find entry to update.');
      }
      
      setEditingEntry(null);
      setEditEntryQty('');
      setEditEntryRate('');
      setEditEntryDate('');
      setEditEntryShift('');
    } catch (error) {
      console.error("Error saving entry edit:", error);
      setMessage('Error saving entry. Please try again.');
    }
  };

  const logout = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading village data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="village-info">
          <h1>🐄 {villageName || decodedVillageCode}</h1>
          <p className="village-subtitle">{decodedVillageCode} - Milk Collection Dashboard</p>
        </div>
        <button onClick={logout} className="logout-btn">
          Logout
        </button>
      </div>

      {message && (
        <div className="message">
          {message}
        </div>
      )}

      <div className="dashboard-content">
        <div className="sidebar">
          <div className="tab-navigation">
            <button 
              className={`tab-btn ${activeTab === 'entry' ? 'active' : ''}`}
              onClick={() => setActiveTab('entry')}
            >
              📝 Milk Entry
            </button>
            <button 
              className={`tab-btn ${activeTab === 'farmers' ? 'active' : ''}`}
              onClick={() => setActiveTab('farmers')}
            >
              👨‍🌾 Manage Farmers
            </button>
            <button 
              className={`tab-btn ${activeTab === 'report' ? 'active' : ''}`}
              onClick={() => setActiveTab('report')}
            >
              📊 Daily Reports
            </button>
            <button 
              className={`tab-btn ${activeTab === 'accounts' ? 'active' : ''}`}
              onClick={() => setActiveTab('accounts')}
            >
              👤 Farmer Accounts
            </button>
          </div>
          
          {/* Recent Entries Aside Section */}
          <div className="recent-entries-section">
            <h3>Recent Entries</h3>
            {recentEntries.length === 0 ? (
              <div className="no-recent-entries">No recent entries</div>
            ) : (
              <div className="recent-entries-list">
                {recentEntries.map((entry, index) => {
                  const farmer = farmers.find(f => f.farmerId === entry.farmerId);
                  return (
                    <div key={index} className="recent-entry-item">
                      <div className="entry-farmer">
                        <strong>{farmer ? farmer.name : entry.farmerId}</strong>
                      </div>
                      <div className="entry-details">
                        <span className="entry-shift">{entry.shift || 'N/A'}</span>
                        <span className="entry-qty">{entry.quantity} L</span>
                        <span className="entry-rate">₹{entry.rate}</span>
                        <span className="entry-total">₹{entry.totalAmount.toFixed(2)}</span>
                      </div>
                      <div className="entry-date">
                        {entry.timestamp?.toDate ? entry.timestamp.toDate().toLocaleDateString() : 
                         entry.timestamp?.seconds ? new Date(entry.timestamp.seconds * 1000).toLocaleDateString() :
                         'N/A'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="main-content">
          {/* Milk Entry Tab */}
          {activeTab === 'entry' && (
            <div className="tab-content">
              <div className="section-header">
                <h2>📝 New Milk Collection</h2>
                <p>Record today's milk collection</p>
              </div>
              
              <div className="entry-form">
                <form onSubmit={addMilkEntry} className="form-grid">
                  <div className="form-group">
                    <label>Select Farmer</label>
                    <select 
                      value={entryFarmerId} 
                      onChange={(e) => setEntryFarmerId(e.target.value)}
                      required
                      className="form-select"
                    >
                      <option value="">Choose a farmer</option>
                      {farmers.map(farmer => (
                        <option key={farmer.farmerId} value={farmer.farmerId}>
                          {farmer.name} ({farmer.farmerId})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Quantity (Liters)</label>
                    <input 
                      type="number" 
                      step="0.1"
                      min="0"
                      placeholder="Enter quantity in liters" 
                      value={qty} 
                      onChange={(e) => setQty(e.target.value)}
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Rate (₹/L)</label>
                    <input 
                      type="number" 
                      step="0.01"
                      min="0"
                      placeholder="Enter rate per liter" 
                      value={rate} 
                      onChange={(e) => setRate(e.target.value)}
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Date</label>
                    <input 
                      type="date" 
                      value={entryDate} 
                      onChange={(e) => setEntryDate(e.target.value)}
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Shift</label>
                    <select 
                      value={entryShift} 
                      onChange={(e) => setEntryShift(e.target.value)}
                      className="form-select"
                    >
                      <option value="morning">Morning</option>
                      <option value="evening">Evening</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Estimated Total</label>
                    <div className="total-display">
                      ₹{ (parseFloat(qty || 0) * parseFloat(rate || 0)).toFixed(2) }
                    </div>
                  </div>
                </form>
                
                <button type="submit" className="submit-btn" onClick={addMilkEntry}>
                  💰 Save Entry
                </button>
              </div>
            </div>
          )}

          {/* Manage Farmers Tab */}
          {activeTab === 'farmers' && (
            <div className="tab-content">
              <div className="section-header">
                <h2>👨‍🌾 Manage Farmers</h2>
                <p>Add and view registered farmers</p>
              </div>
              
              <div className="farmers-section">
                <div className="add-farmer-card">
                  <h3>Add New Farmer</h3>
                  {message && message.includes('Farmer') && (
                    <div className="success-message">{message}</div>
                  )}
                  <form onSubmit={addFarmer} className="farmer-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Farmer Name</label>
                        <input 
                          type="text"
                          placeholder="Enter farmer's full name" 
                          value={newFarmerName} 
                          onChange={(e) => setNewFarmerName(e.target.value)}
                          required
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Farmer ID</label>
                        <input 
                          type="text"
                          placeholder="e.g., F001" 
                          value={newFarmerId} 
                          onChange={(e) => setNewFarmerId(e.target.value.toUpperCase())}
                          required
                          className="form-input"
                          maxLength="10"
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Email Address</label>
                        <input 
                          type="email"
                          placeholder="Enter farmer's email address" 
                          value={newFarmerEmail} 
                          onChange={(e) => setNewFarmerEmail(e.target.value)}
                          required
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Phone Number</label>
                        <input 
                          type="tel"
                          placeholder="Enter farmer's phone number" 
                          value={newFarmerPhone} 
                          onChange={(e) => setNewFarmerPhone(e.target.value)}
                          required
                          className="form-input"
                          pattern="[0-9]{10}"
                          title="Please enter a 10-digit phone number"
                        />
                      </div>
                    </div>
                    <button type="submit" className="add-farmer-btn">
                      ➕ Add Farmer
                    </button>
                  </form>
                </div>

                <div className="farmers-list">
                  <h3>Registered Farmers ({farmers.length})</h3>
                  {farmers.length === 0 ? (
                    <div className="no-farmers">No farmers registered yet.</div>
                  ) : (
                    <div className="farmers-table">
                      <table className="farmers-data-table">
                        <thead>
                          <tr>
                            <th>Farmer Name</th>
                            <th>Farmer ID</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Registered Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {farmers.map((farmer, index) => (
                            <tr key={farmer.id || index}>
                              <td>
                                {editingFarmer === farmer.id ? (
                                  <input 
                                    type="text"
                                    value={editFarmerName}
                                    onChange={(e) => setEditFarmerName(e.target.value)}
                                    className="edit-input"
                                    placeholder="Farmer name"
                                  />
                                ) : (
                                  farmer.name
                                )}
                              </td>
                              <td>
                                {editingFarmer === farmer.id ? (
                                  <input 
                                    type="text"
                                    value={editFarmerId}
                                    onChange={(e) => setEditFarmerId(e.target.value.toUpperCase())}
                                    className="edit-input"
                                    placeholder="Farmer ID"
                                    maxLength="10"
                                  />
                                ) : (
                                  farmer.farmerId
                                )}
                              </td>
                              <td>{farmer.email || 'N/A'}</td>
                              <td>{farmer.phone || 'N/A'}</td>
                              <td>
                                {farmer.createdAt?.toDate().toLocaleDateString()}
                              </td>
                              <td>
                                {editingFarmer === farmer.id ? (
                                  <div className="edit-actions">
                                    <button 
                                      className="save-btn"
                                      onClick={() => saveEditFarmer(farmer.id)}
                                      title="Save Changes"
                                    >
                                      ✅ Save
                                    </button>
                                    <button 
                                      className="cancel-btn"
                                      onClick={cancelEditFarmer}
                                      title="Cancel"
                                    >
                                      ❌ Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <div className="farmer-actions">
                                    <button 
                                      className="edit-btn"
                                      onClick={() => startEditFarmer(farmer)}
                                      title="Edit Farmer"
                                    >
                                      ✏️ Edit
                                    </button>
                                    <button 
                                      className="delete-btn"
                                      onClick={() => deleteFarmer(farmer)}
                                      title="Delete Farmer"
                                    >
                                      🗑️ Delete
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Reports Tab */}
          {activeTab === 'report' && (
            <div className="tab-content">
              <div className="section-header">
                <h2>📊 Daily Reports</h2>
                <p>View collection reports and summaries</p>
              </div>
              
              <div className="reports-section">
                {/* Date Range Report with Merged Farmer Entries */}
                <div className="date-range-section">
                  <h3>📅 Date Range Report (Merged by Farmer)</h3>
                  <div className="date-range-filters">
                    <div className="date-range-group">
                      <label>Date Range:</label>
                      <div className="date-range-inputs">
                        <input 
                          type="date" 
                          value={reportFromDate} 
                          onChange={(e) => setReportFromDate(e.target.value)}
                          className="date-input"
                        />
                        <span>to</span>
                        <input 
                          type="date" 
                          value={reportToDate} 
                          onChange={(e) => setReportToDate(e.target.value)}
                          className="date-input"
                        />
                      </div>
                      <button onClick={generateDateRangeReport} className="generate-btn">
                        📊 Generate Merged Report
                      </button>
                    </div>
                  </div>
                </div>

                {/* Individual Farmer Data Filter */}
                <div className="farmer-specific-section">
                  <h3>👨‍🌾 Individual Farmer Data</h3>
                  <div className="farmer-specific-filters">
                    <div className="farmer-filter-group">
                      <label>Select Farmer:</label>
                      <select 
                        value={selectedFarmerId} 
                        onChange={(e) => setSelectedFarmerId(e.target.value)}
                        className="form-select"
                      >
                        <option value="">Choose a farmer</option>
                        {farmers.map(farmer => (
                          <option key={farmer.farmerId} value={farmer.farmerId}>
                            {farmer.name} ({farmer.farmerId})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="date-range-group">
                      <label>Date Range:</label>
                      <div className="date-range-inputs">
                        <input 
                          type="date" 
                          value={reportFromDate} 
                          onChange={(e) => setReportFromDate(e.target.value)}
                          className="date-input"
                        />
                        <span>to</span>
                        <input 
                          type="date" 
                          value={reportToDate} 
                          onChange={(e) => setReportToDate(e.target.value)}
                          className="date-input"
                        />
                      </div>
                      <button onClick={generateFarmerSpecificReport} className="generate-btn">
                        👨‍🌾 View Farmer Data
                      </button>
                    </div>
                  </div>
                </div>

                {/* Summary Card */}
                {reportData.length > 0 && (
                  <div className="summary-card">
                    <div className="summary-item">
                      <span className="summary-label">Total Records:</span>
                      <span className="summary-value">{reportData.length}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total Quantity:</span>
                      <span className="summary-value">{dailySummary.totalLiters.toFixed(2)} L</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total Amount:</span>
                      <span className="summary-value">₹{dailySummary.totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* Reports Table */}
                <div className="reports-table">
                  {reportData.length === 0 ? (
                    <div className="no-data">No entries found. Select filters and generate a report to view data.</div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Farmer ID</th>
                          <th>Farmer Name</th>
                          <th>Total Quantity</th>
                          <th>Mean Rate</th>
                          <th>Total Amount</th>
                          <th>Entry Count</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.map((entry, index) => {
                          const farmer = farmers.find(f => f.farmerId === entry.farmerId);
                          return (
                            <tr key={index}>
                              <td>{entry.farmerId}</td>
                              <td>{farmer ? farmer.name : 'Unknown'}</td>
                              <td>{entry.totalQuantity.toFixed(2)} L</td>
                              <td>₹{entry.meanRate.toFixed(2)}</td>
                              <td>₹{entry.totalAmount.toFixed(2)}</td>
                              <td>{entry.entries.length}</td>
                              <td>
                                {editingEntry === entry ? (
                                  <div className="edit-entry-actions">
                                    <div className="edit-entry-form">
                                      <input 
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        value={editEntryQty}
                                        onChange={(e) => setEditEntryQty(e.target.value)}
                                        className="edit-entry-input"
                                        placeholder="Qty"
                                      />
                                      <input 
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={editEntryRate}
                                        onChange={(e) => setEditEntryRate(e.target.value)}
                                        className="edit-entry-input"
                                        placeholder="Rate"
                                      />
                                      <input 
                                        type="date"
                                        value={editEntryDate}
                                        onChange={(e) => setEditEntryDate(e.target.value)}
                                        className="edit-entry-input"
                                      />
                                      <select 
                                        value={editEntryShift}
                                        onChange={(e) => setEditEntryShift(e.target.value)}
                                        className="edit-entry-select"
                                      >
                                        <option value="morning">Morning</option>
                                        <option value="evening">Evening</option>
                                      </select>
                                    </div>
                                    <div className="edit-entry-buttons">
                                      <button 
                                        className="save-btn"
                                        onClick={() => saveEditEntry(entry)}
                                        title="Save Changes"
                                      >
                                        ✅ Save
                                      </button>
                                      <button 
                                        className="cancel-btn"
                                        onClick={cancelEditEntry}
                                        title="Cancel"
                                      >
                                        ❌ Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="entry-actions">
                                    <button 
                                      className="edit-btn"
                                      onClick={() => startEditEntry(entry)}
                                      title="Edit Entry"
                                    >
                                      ✏️ Edit
                                    </button>
                                    <button 
                                      className="delete-btn"
                                      onClick={() => deleteEntry(entry)}
                                      title="Delete Entry"
                                    >
                                      🗑️ Delete
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Farmer Accounts Tab */}
          {activeTab === 'accounts' && (
            <div className="tab-content">
              <div className="section-header">
                <h2>👤 Farmer Accounts</h2>
                <p>View and manage farmer account information</p>
              </div>
              
              <div className="accounts-section">
                {/* Farmer Selection */}
                <div className="farmer-selection">
                  <h3>Select Farmer Account</h3>
                  <div className="farmer-selection-form">
                    <div className="form-group">
                      <label>Choose Farmer:</label>
                      <select 
                        value={selectedFarmerId} 
                        onChange={(e) => {
                          setSelectedFarmerId(e.target.value);
                          if (e.target.value) {
                            const farmer = farmers.find(f => f.farmerId === e.target.value);
                            setSelectedFarmer(farmer);
                          } else {
                            setSelectedFarmer(null);
                          }
                        }}
                        className="form-select"
                      >
                        <option value="">Select a farmer to view account</option>
                        {farmers.map(farmer => (
                          <option key={farmer.farmerId} value={farmer.farmerId}>
                            {farmer.name} ({farmer.farmerId})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Account Period:</label>
                      <div className="date-range-inputs">
                        <input 
                          type="date" 
                          value={reportFromDate} 
                          onChange={(e) => setReportFromDate(e.target.value)}
                          className="date-input"
                        />
                        <span>to</span>
                        <input 
                          type="date" 
                          value={reportToDate} 
                          onChange={(e) => setReportToDate(e.target.value)}
                          className="date-input"
                        />
                      </div>
                    </div>
                    <button 
                      onClick={() => {
                        if (selectedFarmerId) {
                          generateFarmerAccountData();
                        } else {
                          setMessage('Please select a farmer to view their account.');
                        }
                      }}
                      className="generate-btn"
                    >
                      👁️ View Account
                    </button>
                  </div>
                </div>

                {/* Farmer Account Details */}
                {selectedFarmer && (
                  <div className="farmer-account-card">
                    <h3>Farmer Account Details</h3>
                    <div className="account-info-grid">
                      <div className="account-info-item">
                        <span className="info-label">Farmer Name:</span>
                        <span className="info-value">{selectedFarmer.name}</span>
                      </div>
                      <div className="account-info-item">
                        <span className="info-label">Farmer ID:</span>
                        <span className="info-value">{selectedFarmer.farmerId}</span>
                      </div>
                      <div className="account-info-item">
                        <span className="info-label">Email:</span>
                        <span className="info-value">{selectedFarmer.email || 'N/A'}</span>
                      </div>
                      <div className="account-info-item">
                        <span className="info-label">Phone:</span>
                        <span className="info-value">{selectedFarmer.phone || 'N/A'}</span>
                      </div>
                      <div className="account-info-item">
                        <span className="info-label">Village Code:</span>
                        <span className="info-value">{selectedFarmer.villageCode}</span>
                      </div>
                      <div className="account-info-item">
                        <span className="info-label">Registered:</span>
                        <span className="info-value">
                          {selectedFarmer.createdAt?.toDate().toLocaleDateString() || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Account Summary */}
                {farmerAccountData.length > 0 && (
                  <div className="account-summary-card">
                    <h3>Account Summary</h3>
                    <div className="summary-grid">
                      <div className="summary-item">
                        <span className="summary-label">Total Entries:</span>
                        <span className="summary-value">{farmerAccountData.length}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Total Quantity:</span>
                        <span className="summary-value">
                          {farmerAccountData.reduce((sum, entry) => sum + entry.quantity, 0).toFixed(2)} L
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Total Amount:</span>
                        <span className="summary-value">
                          ₹{farmerAccountData.reduce((sum, entry) => sum + entry.totalAmount, 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Average Rate:</span>
                        <span className="summary-value">
                          ₹{(
                            farmerAccountData.reduce((sum, entry) => sum + entry.rate, 0) / 
                            farmerAccountData.length
                          ).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Account Entries Table */}
                {farmerAccountData.length > 0 && (
                  <div className="account-entries-table">
                    <h3>Account Entries</h3>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Shift</th>
                          <th>Quantity (L)</th>
                          <th>Rate (₹/L)</th>
                          <th>Total Amount (₹)</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {farmerAccountData.map((entry, index) => (
                          <tr key={index}>
                            <td>{entry.date}</td>
                            <td>{entry.shift || 'N/A'}</td>
                            <td>{entry.quantity.toFixed(2)}</td>
                            <td>₹{entry.rate.toFixed(2)}</td>
                            <td>₹{entry.totalAmount.toFixed(2)}</td>
                            <td>
                              {editingEntry === entry ? (
                                <div className="edit-entry-actions">
                                  <div className="edit-entry-form">
                                    <input 
                                      type="number"
                                      step="0.1"
                                      min="0"
                                      value={editEntryQty}
                                      onChange={(e) => setEditEntryQty(e.target.value)}
                                      className="edit-entry-input"
                                      placeholder="Qty"
                                    />
                                    <input 
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={editEntryRate}
                                      onChange={(e) => setEditEntryRate(e.target.value)}
                                      className="edit-entry-input"
                                      placeholder="Rate"
                                    />
                                    <input 
                                      type="date"
                                      value={editEntryDate}
                                      onChange={(e) => setEditEntryDate(e.target.value)}
                                      className="edit-entry-input"
                                    />
                                    <select 
                                      value={editEntryShift}
                                      onChange={(e) => setEditEntryShift(e.target.value)}
                                      className="edit-entry-select"
                                    >
                                      <option value="morning">Morning</option>
                                      <option value="evening">Evening</option>
                                    </select>
                                  </div>
                                  <div className="edit-entry-buttons">
                                    <button 
                                      className="save-btn"
                                      onClick={() => saveEditEntry(entry)}
                                      title="Save Changes"
                                    >
                                      ✅ Save
                                    </button>
                                    <button 
                                      className="cancel-btn"
                                      onClick={cancelEditEntry}
                                      title="Cancel"
                                    >
                                      ❌ Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="entry-actions">
                                  <button 
                                    className="edit-btn"
                                    onClick={() => startEditEntry(entry)}
                                    title="Edit Entry"
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button 
                                    className="delete-btn"
                                    onClick={() => deleteEntry(entry)}
                                    title="Delete Entry"
                                  >
                                    🗑️ Delete
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {farmerAccountData.length === 0 && selectedFarmer && (
                  <div className="no-account-data">
                    No account data found for {selectedFarmer.name} in the selected date range.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
